import { Injectable } from "@angular/core";
import { forkJoin, Observable, throwError } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { IPC_CHANNEL } from "../../_offline/electron.constants";
import {
  InspectionQueryOpt,
  InspectionSchema,
  InspectionSchemaLocationUpdate,
  InspectionSchemaSharedLockUpdate,
  InspectionSchemaStatusUpdate,
  InspectionSchemaUpdate
} from "../../_offline/models/inspection.schema";
import { STANDALONE } from "../../_offline/standalone-data/standalone-data";
import { ElectronService } from "../../electron.service";
import { AssetModuleClient } from "../asset-module/asset-module-client.service";
import { DatabaseBaseService } from "../base/database-base.service";
import { FeatureFlagOfflineService } from "../feature-flag/feature-flag.offline.service";
import { LoginUserOfflineService } from "../offline/login-user-offline.service";
import { SubjectFilter } from "../storage.service";

@Injectable({
  providedIn: "root"
})
export class InspectionDbService extends DatabaseBaseService {
  get userId() {
    return this.loginUserService.loginUser?.id;
  }

  constructor(
    protected electronService: ElectronService,
    protected loginUserService: LoginUserOfflineService,
    private featureFlagService: FeatureFlagOfflineService,
    private assetModuleClient: AssetModuleClient
  ) {
    super(electronService, loginUserService);
  }

  get assetModuleEnabled() {
    return this.featureFlagService.isFlagEnabled("asset_module")();
  }

  getListAll(
    statusCode?: string,
    subjectFilter?: SubjectFilter,
    isOwn?: boolean,
    sort?: { active: string; direction: string }
  ): Observable<InspectionSchema[]> {
    const inspOpt = new InspectionQueryOpt(this.tenantId, this.userId, statusCode, subjectFilter, isOwn, sort);
    if (this.assetModuleEnabled) {
      return forkJoin({
        inspections: this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_LIST_ALL, inspOpt),
        liveAssets: this.assetModuleClient.getLiveAssets(this.tenantId)
      }).pipe(
        map(({ inspections, liveAssets }) =>
          inspections.map((inspection: InspectionSchema) => {
            const subject = liveAssets.find((a) => a.id === InspectionSchema.effectiveAssetId(inspection, this.assetModuleEnabled));
            if (subject) {
              inspection.subject = subject;
              inspection.subjectId = InspectionSchema.effectiveAssetId(inspection, this.assetModuleEnabled);
            }
            return inspection;
          })
        )
      );
    } else {
      return this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_LIST_ALL, inspOpt);
    }
  }

  getAllOnPremise(): Observable<InspectionSchema[]> {
    const inspOpt = new InspectionQueryOpt(STANDALONE.TENANT_ID, STANDALONE.USER_ID);
    if (this.featureFlagService.isFlagEnabled("asset_module")()) {
      return forkJoin({
        inspections: this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_LIST_ALL, inspOpt),
        liveAssets: this.assetModuleClient.getLiveAssets(this.tenantId)
      }).pipe(
        map(({ inspections, liveAssets }) =>
          inspections.map((inspection: InspectionSchema) => {
            const subject = liveAssets.find((a) => a.id === InspectionSchema.effectiveAssetId(inspection, this.assetModuleEnabled));
            if (subject) {
              inspection.subject = subject;
            }
            return inspection;
          })
        )
      );
    } else {
      return this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_LIST_ALL, inspOpt);
    }
  }

  getListById(inspectionId: string[], isAliveOnly = false): Observable<InspectionSchema[]> {
    const inspections$ = this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_LIST_BY_ID, this.tenantId, this.userId, inspectionId, isAliveOnly);
    if (!this.assetModuleEnabled) {
      return inspections$;
    }
    return forkJoin({
      inspections: inspections$,
      liveAssets: this.assetModuleClient.getLiveAssets(this.tenantId)
    }).pipe(
      map(({ inspections, liveAssets }) =>
        inspections.map((inspection: InspectionSchema) => {
          const subject = liveAssets.find((a) => a.id === InspectionSchema.effectiveAssetId(inspection, this.assetModuleEnabled));
          if (subject) {
            inspection.subject = subject;
            inspection.subjectId = InspectionSchema.effectiveAssetId(inspection, this.assetModuleEnabled);
          }
          return inspection;
        })
      )
    );
  }

  getInspection(id: string, aliveOnly: boolean, tenantId?: string, isForce = false): Observable<InspectionSchema> {
    const resolvedTenantId = tenantId ?? this.tenantId;
    if (this.featureFlagService.isFlagEnabled("asset_module")()) {
      return this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_ONE, resolvedTenantId, this.userId, id, aliveOnly, isForce).pipe(
        switchMap((inspection: InspectionSchema) => {
          if (!inspection) {
            return throwError(() => new Error(`Inspection ${id} was not found in the local database`));
          }
          return this.assetModuleClient.getLiveAsset(InspectionSchema.effectiveAssetId(inspection, this.assetModuleEnabled), this.tenantId).pipe(
            map((subject) => {
              inspection.subject = subject;
              inspection.subjectId = subject.id;

              return inspection;
            })
          );
        })
      );
    }
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_ONE, resolvedTenantId, this.userId, id, aliveOnly, isForce);
  }

  getInspectionWithDetails(id: string, aliveOnly: boolean, tenantId?: string, isForce = false): Observable<InspectionSchema> {
    const resolvedTenantId = tenantId ?? this.tenantId;
    if (this.featureFlagService.isFlagEnabled("asset_module")()) {
      return this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_ONE, resolvedTenantId, this.userId, id, aliveOnly, isForce).pipe(
        switchMap((inspection: InspectionSchema) => {
          if (!inspection) {
            return throwError(() => new Error(`Inspection ${id} was not found in the local database`));
          }
          return this.assetModuleClient.getLiveAssetAsSubject(InspectionSchema.effectiveAssetId(inspection, this.assetModuleEnabled), this.tenantId).pipe(
            map((subject) => {
              inspection.subject = subject;
              inspection.subjectId = subject.id;

              return inspection;
            })
          );
        })
      );
    }
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.GET_ONE, resolvedTenantId, this.userId, id, aliveOnly, isForce);
  }

  putListAll(inspectionList: InspectionSchema[]): Observable<InspectionSchema[]> {
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.PUT_LIST_ALL, inspectionList);
  }

  addInspection(inspection: InspectionSchema): Observable<InspectionSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.PUT_ONE, inspection);
  }

  updateInspection(inspection: InspectionSchemaUpdate): Observable<InspectionSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.PUT_ONE, inspection);
  }

  updateInspectionLocations(inspection: InspectionSchemaLocationUpdate): Observable<InspectionSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.PUT_ONE, inspection);
  }

  updateInspectionStatus(inspection: InspectionSchemaStatusUpdate): Observable<InspectionSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.PUT_ONE, inspection);
  }

  updateInspectionSharedLock(inspection: InspectionSchemaSharedLockUpdate): Observable<InspectionSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.PUT_ONE, inspection);
  }

  deleteInspection(inspectionId: string, isLogicalDelete = true): Observable<InspectionSchema> {
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.DELETE_ONE, inspectionId, isLogicalDelete);
  }

  hasInspections(subjectId: string): Observable<boolean> {
    return this.ipcSendSync(IPC_CHANNEL.INSPECTION.IS_INSPECTION_USING_SUBJECTID_EXISTS, subjectId, this.tenantId).pipe(map((x) => x as boolean));
  }
}
